import { useState } from "react";
import { Calendar, Users, ArrowRight } from "lucide-react";
import Button from "@/components/ui/Button";
import type { BookingDraft } from "@/types/booking";
import { cn } from "@/lib/utils/cn";

interface StepDatesProps {
  draft: BookingDraft;
  onNext: (
    checkIn: string,
    checkOut: string,
    adults: number,
    children: number,
  ) => void;
}

const fmt = (d: Date) => d.toISOString().split("T")[0];
const today = fmt(new Date());

export default function StepDates({ draft, onNext }: StepDatesProps) {
  const [checkIn, setCheckIn] = useState(draft.checkIn);
  const [checkOut, setCheckOut] = useState(draft.checkOut);
  const [adults, setAdults] = useState(draft.adults);
  const [children, setChildren] = useState(draft.children);
  const [error, setError] = useState("");

  const handleNext = () => {
    if (!checkIn || !checkOut) {
      setError("Please select check-in and check-out dates.");
      return;
    }
    if (checkOut <= checkIn) {
      setError("Check-out must be after check-in.");
      return;
    }
    setError("");
    onNext(checkIn, checkOut, adults, children);
  };

  const fieldClass = cn(
    "flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-4",
    "focus-within:border-metro-green-800 focus-within:ring-2 focus-within:ring-metro-green-800/10 transition",
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl text-metro-green-950 mb-1">
          When are you visiting?
        </h2>
        <p className="text-sm text-metro-ink-subtle">
          Select your dates and number of guests.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {/* Check-in */}
        <div className={fieldClass}>
          <Calendar size={18} className="shrink-0 text-metro-green-800" />
          <div className="flex-1">
            <label className="block text-[10px] font-medium uppercase tracking-widest text-metro-ink-subtle">
              Check In
            </label>
            <input
              type="date"
              value={checkIn}
              min={today}
              onChange={(e) => {
                setCheckIn(e.target.value);
                // Auto-push checkout if it's now before check-in
                if (checkOut <= e.target.value) {
                  const next = new Date(e.target.value);
                  next.setDate(next.getDate() + 1);
                  setCheckOut(fmt(next));
                }
              }}
              className="w-full bg-transparent text-sm font-medium text-metro-green-950 outline-none"
            />
          </div>
        </div>

        {/* Check-out */}
        <div className={fieldClass}>
          <Calendar size={18} className="shrink-0 text-metro-green-800" />
          <div className="flex-1">
            <label className="block text-[10px] font-medium uppercase tracking-widest text-metro-ink-subtle">
              Check Out
            </label>
            <input
              type="date"
              value={checkOut}
              min={checkIn}
              onChange={(e) => setCheckOut(e.target.value)}
              className="w-full bg-transparent text-sm font-medium text-metro-green-950 outline-none"
            />
          </div>
        </div>
      </div>

      {/* Guests */}
      <div className={fieldClass}>
        <Users size={18} className="shrink-0 text-metro-green-800" />
        <div className="flex flex-1 flex-wrap gap-6">
          {/* Adults */}
          <div>
            <p className="text-[10px] font-medium uppercase tracking-widest text-metro-ink-subtle mb-2">
              Adults
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setAdults((a) => Math.max(1, a - 1))}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-metro-ink-muted transition hover:border-metro-green-800 hover:text-metro-green-800"
              >
                −
              </button>
              <span className="w-4 text-center text-sm font-medium text-metro-green-950">
                {adults}
              </span>
              <button
                type="button"
                onClick={() => setAdults((a) => Math.min(10, a + 1))}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-metro-ink-muted transition hover:border-metro-green-800 hover:text-metro-green-800"
              >
                +
              </button>
            </div>
          </div>

          {/* Children */}
          <div>
            <p className="text-[10px] font-medium uppercase tracking-widest text-metro-ink-subtle mb-2">
              Children
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setChildren((c) => Math.max(0, c - 1))}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-metro-ink-muted transition hover:border-metro-green-800 hover:text-metro-green-800"
              >
                −
              </button>
              <span className="w-4 text-center text-sm font-medium text-metro-green-950">
                {children}
              </span>
              <button
                type="button"
                onClick={() => setChildren((c) => Math.min(6, c + 1))}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-metro-ink-muted transition hover:border-metro-green-800 hover:text-metro-green-800"
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <div className="flex justify-end">
        <Button rightIcon={<ArrowRight size={16} />} onClick={handleNext}>
          Check Availability
        </Button>
      </div>
    </div>
  );
}